




























import { Component, Prop, Vue } from "vue-property-decorator";
import { RENO_QUALITY_RATINGS } from "@/data/models/RenoReviewEntry";
import {
  COLORS_QUALITY_RATING_SCALE,
  COLORS_QUALITY_RATING_SCALE_DARK,
  COLOR_NOT_SELECTED_QUALITY_RATING,
  COLOR_NOT_SELECTED_QUALITY_RATING_DARK,
  TEXT_CLASSES_QUALITY_RATING_SCALE,
  TEXT_CLASSES_QUALITY_RATING_SCALE_DARK,
} from "@/utils/colors";
import { themed } from "@/utils/Theme";

/**
 * Wording for ratings 1-4, worst first.
 *
 * Reuses the drying-quality scale the rate-drying screens already show
 * (`schlecht / befriedigend / gut / sehr gut`), which is a 1-4 bad-to-good scale and therefore maps
 * onto the ticket's exactly. Reviewers read the same words here as everywhere else, and no new
 * strings are invented for a scale that already has them.
 */
const RATING_LABEL_KEYS = ["veryBad", "bad", "good", "veryGood"];

/**
 * The 1-4 quality scale, rendered as four buttons.
 *
 * Presentation only: the parent decides what pressing one means, which keeps "a failed write must not
 * advance the pass" in one place and lets the filter screen use the same control for a multi-select.
 */
@Component
export default class RenoReviewRatingComponent extends Vue {
  /** Which steps are filled. One value in the stepper, any number on the filter screen. */
  @Prop({ default: () => [] }) selectedValues!: number[];

  /** Blocks input, e.g. while a save is in flight so a fast reviewer cannot outrun the write. */
  @Prop({ default: false }) disabled!: boolean;

  /**
   * The stepper's footer below 960 px: the scale takes its own line and the four cells share it, at
   * most their 170 px each, so the last one is no longer cut off on an iPad (2026-11-03).
   */
  @Prop({ type: Boolean, default: false }) fluid!: boolean;

  /** Which button shows the spinner: the one that was pressed, not all four. */
  @Prop({ default: null }) pendingValue!: number | null;

  /**
   * Whether a filled step also gets a tick.
   *
   * On for a multi-select, where several fills at once make "chosen" less obvious than a single
   * highlighted step is; off in the stepper, where the one fill is the stored verdict.
   */
  @Prop({ type: Boolean, default: false }) showSelectedIcon!: boolean;

  get ratingOptions() {
    return RENO_QUALITY_RATINGS.map((value, index) => {
      const isSelected = this.selectedValues.includes(value);

      return {
        value,
        isSelected,
        label: this.$t(`rateDrying.qualityDrying.${RATING_LABEL_KEYS[index]}`).toString(),
        color: isSelected ? this.scaleColors[index] : this.unselectedColor,
        // Forced per fill: Vuetify leaves the label at the theme default over an arbitrary hex, so
        // without this the same button reads in one theme and vanishes in the other.
        textClass: isSelected ? this.scaleTextClasses[index] : "",
      };
    });
  }

  /**
   * Fill behind the steps that are not selected.
   *
   * Theme-dependent because the pale light-mode grey swallows the white label the dark theme applies
   * to buttons.
   */
  /** The four fills for the active theme: in dark mode palette shades, with a lime "good". */
  get scaleColors(): string[] {
    return this.$vuetify.theme.dark ? COLORS_QUALITY_RATING_SCALE_DARK : COLORS_QUALITY_RATING_SCALE;
  }

  get scaleTextClasses(): string[] {
    return this.$vuetify.theme.dark ? TEXT_CLASSES_QUALITY_RATING_SCALE_DARK : TEXT_CLASSES_QUALITY_RATING_SCALE;
  }

  get unselectedColor(): string {
    return themed(
      COLOR_NOT_SELECTED_QUALITY_RATING,
      COLOR_NOT_SELECTED_QUALITY_RATING_DARK
    );
  }
}
